import { React } from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import EventOptions from "Events/Components/EventOptions";
import Card from "shared/Card";
import EventImage from "Events/Components/EventImage";
import styles from "./EventCard.module.css";
import classNames from "classnames";
import moment from "moment-timezone";

/**
 * Component that displays the events information
 */
export function EventCard({ event, onClone, onArchive }) {
  const history = useHistory();
  const isEventArchived = event.status === "Archived";

  const timezoneLabelToIana = {
    "Pacific Time (PT)": "America/Los_Angeles",
    "Mountain Time (MT)": "America/Boise",
    "Central Time (CT)": "America/Chicago",
    "Eastern Time (ET)": "America/New_York",
    "Alaska Time (AKT)": "America/Anchorage",
    "Hawaii-Aleutian Time (HAT)": "America/Adak",
    "Hawaii-Honolulu (HST)": "Pacific/Honolulu",
    "Australian Eastern Standard Time (AEST)": "Australia/Sydney",
    "Australian Central Standard Time (ACST)": "Australia/Adelaide",
    "Australian Western Standard Time (AWST)": "Australia/Perth",
    "New Zealand Standard Time (NZST)": "Pacific/Auckland",
    "Australian Eastern Daylight Time (AEDT)": "Australia/Sydney",
    "New Zealand Daylight Time (NZDT)": "Pacific/Auckland",
  };

  const toIanaTimeZone = (timeZone) =>
    timezoneLabelToIana[timeZone] || timeZone || "America/Los_Angeles";

  const eventTimeZone = toIanaTimeZone(event.eventStartTimeZone);

  const formatDateTimeInEventTimeZone = (utcDate) =>
    utcDate
      ? moment.utc(utcDate).tz(eventTimeZone).format("MM/DD/YYYY h:mm A")
      : "";

  const handleView = () => {
    switch (event.ruleset) {
      case "Major v1.0":
        history.push(`view/event/majorv1/${event.id}`);
        break;
      case "Local v1.0":
        history.push(`view/event/localv1/${event.id}`);
        break;
      case "LOS v1.0":
        history.push(`view/event/losv1/${event.id}`);
        break;
      case "Major v2.0":
        history.push(`view/event/majorv2/${event.id}`);
        break;
      case "Local v2.0":
        history.push(`view/event/localv2/${event.id}`);
        break;
      case "LOS v2.0":
        history.push(`view/event/losv2/${event.id}`);
        break;
      case "LOS v3.0":
        history.push(`view/event/losv3/${event.id}`);
        break;
      default:
        history.push(`view/event/${event.id}`);
    }
  };

  const handleEdit = () => {
    switch (event.ruleset) {
      case "Major v1.0":
        history.push(`edit/event/majorv1/${event.id}`);
        break;
      case "Local v1.0":
        history.push(`edit/event/localv1/${event.id}`);
        break;
      case "LOS v1.0":
        history.push(`edit/event/losv1/${event.id}`);
        break;
      case "Major v2.0":
        history.push(`edit/event/majorv2/${event.id}`);
        break;
      case "Local v2.0":
        history.push(`edit/event/localv2/${event.id}`);
        break;
      case "LOS v2.0":
        history.push(`edit/event/losv2/${event.id}`);
        break;
      case "LOS v3.0":
        history.push(`edit/event/losv3/${event.id}`);
        break;
      default:
        history.push(`edit/${event.id}`);
    }
  };

  const handleArchive = () => {
    onArchive(event.id);
  };

  const formatName = (name) => {
    if (name && name.includes(",")) {
      const [lastName, firstName] = name.split(",").map((part) => part.trim());
      return `${firstName} ${lastName}`;
    }
    return name;
  };

  const formatHostOrSpeaker = (items = []) =>
    items
      .map((item) => (item.name ? formatName(item.name) : ""))
      .filter(Boolean);

  const hosts = formatHostOrSpeaker(event.hosts);
  const speakers = formatHostOrSpeaker(event.speakers);

  const hostsAndSpeakers = [...hosts, ...speakers].join(", ");

  const cardContents = (
    <div className={styles.eventCardContents}>
      <div className={styles.dateAndImageContainer}>
        <div className={styles.eventImage}>
          {event.ruleset.includes("Major") && (
            <EventImage image={event.image} ruleset="Major" />
          )}
          {event.ruleset.includes("Local") && (
            <EventImage image={event.image} ruleset="Local" />
          )}
          {event.ruleset.includes("LOS") && (
            <EventImage image={event.image} ruleset="LOS" />
          )}
        </div>
      </div>

      <div className={styles.eventInfoContainer}>
        <div className={classNames(styles.eventInfoItem, styles.bold)}>
          {event.name +
            " - " +
            (event.venue == "onsite" ? "Onsite" : "Virtual")}
        </div>

        {event.title ? (
          <div className={styles.eventInfoName}>{event.title}</div>
        ) : (
          <div className={styles.eventInfoName}>Display Name: Not Found</div>
        )}

        {hostsAndSpeakers && (
          <div className={styles.eventInfoName}>{hostsAndSpeakers}</div>
        )}

        {event.startDate ? (
          <div className={styles.eventInfoDate}>
            {formatDateTimeInEventTimeZone(event.startDate)}{" "}
            {event.eventStartTimeZone}
            {event.endDate && " - "}
            {event.endDate && formatDateTimeInEventTimeZone(event.endDate)}{" "}
            {event.endDate && event.eventStartTimeZone}
          </div>
        ) : (
          <div className={styles.eventInfoDate}>
            Start / End Date: Not Found
          </div>
        )}

        <div className={styles.eventInfoItem}>{event.location}</div>
      </div>

      <div className={styles.ticketsSoldInfo}>
        <div>
          {isNaN(event.ticketsSold) ||
          isNaN(event.totalTickets) ||
          event.ticketsSold == 0 ||
          event.totalTickets == 0
            ? ""
            : event.ticketsSold + "/" + event.totalTickets}
        </div>
      </div>

      <div className={styles.status}>{event.status}</div>

      <div className={styles.eventOptions}>
        <EventOptions
          onView={() => handleView()}
          onEdit={() => handleEdit()}
          onClone={onClone}
          onArchive={() => handleArchive()}
          isArchived={isEventArchived}
        />
      </div>
    </div>
  );

  return <Card className={styles.eventCardContainer} children={cardContents} />;
}

EventCard.propTypes = {
  event: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    image: PropTypes.string,
    name: PropTypes.string,
    location: PropTypes.string,
    jobCode: PropTypes.string,
    ticketsSold: PropTypes.number,
    totalTickets: PropTypes.number,
    status: PropTypes.string,
    eventStartTimeZone: PropTypes.string,
  }),
};
